import { useCallback, useEffect, useState } from "react";
import { notificationsApi } from "../services/notifications.api";
import type {
  NotificationItem,
  NotificationListParams,
  NotificationListResult,
} from "../types/notifications.types";

/**
 * useNotifications — paginated notification list.
 * Plain state + axios (this project does not use React Query).
 */
export function useNotifications(initial?: NotificationListParams) {
  const [params, setParams] = useState<NotificationListParams>({
    page: initial?.page ?? 1,
    limit: initial?.limit ?? 10,
    unreadOnly: initial?.unreadOnly ?? false,
  });
  const [data, setData] = useState<NotificationListResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchList = useCallback(
    async (next?: NotificationListParams) => {
      setIsLoading(true);
      setError(null);
      try {
        const merged = { ...params, ...(next ?? {}) };
        const result = await notificationsApi.listNotifications(merged);
        setData(result);
        if (next) setParams(merged);
        return result;
      } catch (e) {
        setError(e);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [params],
  );

  useEffect(() => {
    void fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    items: (data?.items ?? []) as NotificationItem[],
    total: data?.count ?? 0,
    unreadCount: data?.unreadCount ?? 0,
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    unreadOnly: params.unreadOnly ?? false,
    isLoading,
    error,
    refetch: fetchList,
    setPage: (page: number) => fetchList({ page }),
    setUnreadOnly: (unreadOnly: boolean) => fetchList({ unreadOnly, page: 1 }),
  };
}
